// src/OrderTrackingClassic.js
import React, { useState, useEffect, useRef } from "react";
import Table from 'components/Table/Table';
import { useHistory, useLocation } from "react-router-dom";
import moment from "moment";
import CloseIcon from '@material-ui/icons/Close';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined'
import CloseOutlinedIcon from "@material-ui/icons/CloseOutlined";
import {
	DialogActions,
	DialogTitle,
	Box,
	withStyles,
	TextField,
	IconButton,
	Dialog,
	DialogContent,
	DialogContentText,
	InputAdornment,
	Button,
	Badge,
	Grid,
	MenuItem,
	Typography,
	InputLabel
} from "@material-ui/core";
import { blockInvalidChar } from "helpers/blockInvalidChar";
import { Styles } from "../styles";
import searchIcon from "assets/img/Search_Icon.png";
import {
	DatePicker,
	MuiPickersUtilsProvider,
	KeyboardDatePicker,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { enUS, fr } from "date-fns/locale";
import { useTranslation } from 'react-i18next';
import ThriveSignatureRequests from 'apis/ThriveSignatureRequest/ThriveSignatureRequest';
import { toast } from 'react-toastify';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import { format } from "date-fns";


// custom TextField
const CssTextField = withStyles({
	root: {
		'& label.Mui-focused': { color: '##D3D3D3' },
		'& .MuiFormLabel-root': { color: '#707070' },
		'& .MuiInputBase-input': { color: '#707070' },
		'& .MuiInput-underline:before': { borderBottomColor: '#707070!important' },
		'& .MuiInput-underline:after': { borderBottomColor: '#707070!important' },
		'& .MuiOutlinedInput-root': {
			'& fieldset': { borderColor: '#707070' },
			'&:hover fieldset': { borderColor: '#707070' },
			'&.Mui-focused fieldset': { borderColor: '#707070' },
			'&.MuiOutlinedInput-root': { marginTop: 0, borderRadius: 20, },
		},
	},
})(TextField);
const ButtonLoader = () => (
	<div className="snippet" style={{ marginLeft: 25 }}>
		<div className="stage"><div className="dot-typing-p"></div></div>
	</div>
);

// ── Step definitions ──────────────────────────────────────────────────────────
// Logs are ALWAYS matched by `step_name` (never by step_id), because Unlocked / Trial
// logs use a different step_id numbering (Internal Notification is removed).
const STEP_NAMES = {
	request: 'Request Received',
	verification: 'Account Verification',
	internal: 'Internal Notification',
	assignment: 'User Assignment',
	welcome: 'Welcome Notification',
	billing: 'Billing Verification',
	addon: 'Add-on Plans',
};

const CLASSIC_STEPS = ['request', 'verification', 'internal', 'assignment', 'welcome', 'billing', 'addon'];
const UNLOCK_TRIAL_STEPS = ['request', 'verification', 'assignment', 'welcome', 'addon'];

export default function OrderTrackingClassic({ setDialogObj, requestId, shipto, handleGet, email, plan }) {
	const [expandedSteps, setExpandedSteps] = useState({});
	const { t, i18n } = useTranslation();
	const classes = Styles();

	const [formValues, setFormValues] = useState({
		salesRepIds: [''],
		billingDate: moment(new Date()).format("YYYY-MM-DD"),
		salesPlan: 'Skip',
		freeFreight: 'Apply',
		accountVerified: 'Yes',
		editedShipto: shipto,
		editedEmail: email,
		comments: ''
	});

	const [error, setError] = useState({});
	const [states, setStates] = useState({ date: new Date() });
	const [reason, setReason] = useState('');
	const [openReasonDialog, setOpenReasonDialog] = useState(false);
	const [approveLoading, setApproveLoading] = useState(false);
	const [rejectLoading, setRejectLoading] = useState(false);
	const [welcomeLoading, setWelcomeLoading] = useState(false);
	const [billingLoading, setBillingLoading] = useState(false);
	const [addonLoading, setAddonLoading] = useState(false);
	const [reasonSubmitLoading, setReasonSubmitLoading] = useState(false);

	const [data, setData] = useState([]);
	const [logsLoaded, setLogsLoaded] = useState(false);
	// const [pms, setpms] = useState(null);

	const { getLogs, salesRepMappingApi, billingConfirmationApi, addOnPlansMappingApi, salesRepRejectAPI } = ThriveSignatureRequests();
	const locale = i18n.language == 'fr' ? fr : enUS;

	const {
		salesRepIds, billingDate, salesPlan, freeFreight,
		accountVerified, editedShipto, editedEmail, comments
	} = formValues;

	const isUnlocked = plan === 'Unlocked';

	const isTrail = plan === 'Trial';

	// Internal Notification is not shown for Unlocked / Trial
	const hideInternalNotification = isUnlocked || isTrail;

	// ── Step state is derived from the logs (by step_name), not from backend activeStep ──
	const visibleSteps = hideInternalNotification ? UNLOCK_TRIAL_STEPS : CLASSIC_STEPS;

	const logsFor = (key) => (data || []).filter(i => i.step_name == STEP_NAMES[key]);
	const isDone = (key) => logsFor(key).length > 0;
	const lastLogDate = (key) => logsFor(key).slice(-1)[0]?.createdAt;

	// Number shown inside the circle = position in the visible list
	const numberOf = (key) => visibleSteps.indexOf(key) + 1;

	// First visible step that has no logs yet
	const firstIncompleteKey = visibleSteps.find(k => !isDone(k));

	// Active step:
	//  - the first incomplete step, and
	//  - Classic only: Add-on Plans can be done any time after Welcome (in parallel with Billing Verification)
	const isActive = (key) => {
		if (!logsLoaded || isDone(key)) return false;
		if (key === firstIncompleteKey) return true;
		return !hideInternalNotification && key === 'addon' && isDone('welcome');
	};

	// Only completed steps can be collapsed / re-opened. Active step is always open.
	const toggleStep = (key) => {
		if (isDone(key)) {
			setExpandedSteps(prev => ({ ...prev, [key]: !prev[key] }));
		}
	};

	const stepProps = (key) => ({
		stepKey: key,
		displayNumber: numberOf(key),
		isActive: isActive(key),
		isCompleted: isDone(key),
		expanded: !!expandedSteps[key],
		toggleStep,
		date: lastLogDate(key),
	});

	const renderSimpleLogs = (key) => (
		<ul>
			{logsFor(key).map((d, idx) => (
				<li key={idx}>
					<div>
						<span style={{ color: 'black' }}>{d.notes}</span><br />
						<span style={{ fontStyle: 'italic' }}>{moment(d.createdAt).format("ddd, Do MMM 'YY - h:mm A")}</span>
					</div>
				</li>
			))}
		</ul>
	);

	// ── Dialog open/close state ───────────────────────────────────────────────
	const [dormantDialog, setDormantDialog]             = useState({ open: false });
	const [unlockDialog, setUnlockDialog]               = useState({ open: false }); // not in whitelist
	const [inactiveUnlockDialog, setInactiveUnlockDialog] = useState({ open: false }); // in whitelist but inactive/ineligible

	// ── Pending action after a dialog confirmation ────────────────────────────
	// We store what the user already answered so we can pass it on the next call.
	const [pendingReactivate, setPendingReactivate] = useState(false);
	const [pendingConfirmUnlock, setPendingConfirmUnlock] = useState(false);
	const approveSubmittingRef = useRef(false);   // Approve + Dormant/Unlock/Inactive-Unlock confirms (all re-invoke the approve flow)
	const rejectSubmittingRef  = useRef(false);   // Reject reason-dialog Submit
	const welcomeSubmittingRef = useRef(false);   // Welcome Confirm
	const billingSubmittingRef = useRef(false);   // Billing Confirm
	const addonSubmittingRef   = useRef(false);   // Add-on Confirm
	const guardedSubmit = (ref, fn) => async (...args) => {
		if (ref.current) return;
		ref.current = true;
		try {
			await fn(...args);
		} finally {
			ref.current = false;
		}
	};

	useEffect(() => { handleGetLogs(); }, []);

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormValues(prev => ({ ...prev, [name]: value }));
	};

	// ── Multiple Sales Rep Email handling ─────────────────────────────────────
	const handleSalesRepIdChange = (index, value) => {
		setFormValues(prev => {
			const updated = [...prev.salesRepIds];
			updated[index] = value;
			return { ...prev, salesRepIds: updated };
		});

		// live duplicate check (case-insensitive, ignores blanks)
		const normalized = value.trim().toLowerCase();
		if (normalized) {
			const duplicateExists = salesRepIds.some(
				(rep, i) => i !== index && rep.trim().toLowerCase() === normalized
			);
			if (duplicateExists) {
				setError(prev => ({ ...prev, salesRepId: "Duplicate Sales Rep Email is not allowed." }));
				return;
			}
		}
		setError(prev => ({ ...prev, salesRepId: '' }));
	};

	const addSalesRepId = () => {
		const trimmedReps = salesRepIds.map(r => r.trim()).filter(Boolean);
		const lastEntry = salesRepIds[salesRepIds.length - 1]?.trim();

		if (!lastEntry) {
			toast.error("Please fill the current Sales Rep Email before adding another.");
			return;
		}

		const hasDuplicates = new Set(trimmedReps.map(r => r.toLowerCase())).size !== trimmedReps.length;
		if (hasDuplicates) {
			toast.error("Duplicate Sales Rep Email is not allowed.");
			return;
		}

		setFormValues(prev => ({ ...prev, salesRepIds: [...prev.salesRepIds, ''] }));
	};

	const removeSalesRepId = (index) => {
		setFormValues(prev => {
			if (prev.salesRepIds.length === 1) return prev; // keep at least one field
			const updated = prev.salesRepIds.filter((_, i) => i !== index);
			return { ...prev, salesRepIds: updated };
		});
		setError(prev => ({ ...prev, salesRepId: '' }));
	};

	const handleGetLogs = async () => {
		try {
			let values = {
				requestId,
				type: plan === 'Unlocked' ? 'Unlock Enrollment' : "Enrollment",
				shipto,
			};
			const res = await getLogs(values);
			if (res) {
				setData(res.data || []);
				setLogsLoaded(true);
				const unlockDate = res.unlockDate ? new Date(res.unlockDate) : new Date();
				setStates(prev => ({
					...prev,
					minEnrollmentDate: res.enrollmentDate,
					minBillingDate: res.billingDate,
					date: unlockDate,
				}));
			}
		} catch (e) {
			console.error('Get error:', e);
		}
	};

	const handleSubmitSalesRepId = async (reactivate = false, confirmUnlock = false) => {
		const trimmedReps = salesRepIds.map(r => r.trim()).filter(Boolean);

		if (trimmedReps.length === 0) {
			setError(prev => ({ ...prev, salesRepId: "At least one Sales Rep Email is required." }));
			return;
		}

		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		const invalidRep = trimmedReps.find(r => !emailRegex.test(r));
		if (invalidRep) {
			setError(prev => ({ ...prev, salesRepId: `"${invalidRep}" is not a valid email address.` }));
			return;
		}

		const lowerCasedReps = trimmedReps.map(r => r.toLowerCase());
		const hasDuplicates = new Set(lowerCasedReps).size !== lowerCasedReps.length;
		if (hasDuplicates) {
			setError(prev => ({ ...prev, salesRepId: "Duplicate Sales Rep Email is not allowed." }));
			return;
		}

		if (!editedEmail || !editedEmail.trim()) {
			setError(prev => ({ ...prev, salesRepId: "Customer Email is required." }));
			return;
		}
		if (!editedShipto || !editedShipto.trim()) {
			setError(prev => ({ ...prev, salesRepId: "Shipto is required." }));
			return;
		}
		if (!emailRegex.test(editedEmail)) {
			setError(prev => ({ ...prev, salesRepId: "Please enter a valid customer email address." }));
			return;
		}
		if (!shipto || !requestId) {
			toast.error("Missing required data (shipto or requestId).");
			return;
		}
		if (!states?.date || states?.date == 'Invalid date') {
			toast.error("Please select the start date");
			return;
		}

		try {
			setApproveLoading(true);

			const resolvedShipto = (shipto === editedShipto) ? shipto : editedShipto;

			const values = {
				salesRepIds: trimmedReps,
				shipto: resolvedShipto,
				requestId,
				startDate: states.date
				? format(states.date, "yyyy-MM-dd")
				: null,
				accountVerified,
				reactivate,
				comments,
				// pms,
				confirmUnlock,
				...(editedEmail !== email && { editedEmail }),
			};

			const res = await salesRepMappingApi(values);
			setApproveLoading(false);

			if (res.result_status == 0) {
				toast.error(res.message);
				return;
			}

			if (res.result_status == 2) {
				setPendingConfirmUnlock(confirmUnlock); // remember current unlock answer
				setDormantDialog({ open: true });
				return;
			}

			if (res.result_status == 3) {
				setUnlockDialog({ open: true });
				return;
			}

			if (res.result_status == 4) {
				setInactiveUnlockDialog({ open: true });
				return;
			}

			if (res?.message === "Verification completed successfully") {
				setDialogObj({ open: false, obj: {}, index: null });
				if (res?.courtesyPoints > 0) {
					toast.success(`You have been given ${res.courtesyPoints} points due to back date.`);
				}
				handleGet();
			}

		} catch (e) {
			console.error('API Error:', e);
			setApproveLoading(false);
			toast.error("An error occurred while submitting.");
		}
	};

	const handleDormantConfirm = () => {
		setDormantDialog({ open: false });
		setPendingReactivate(true);
		guardedSubmit(approveSubmittingRef, handleSubmitSalesRepId)(true, pendingConfirmUnlock);
	};

	const handleDormantCancel = () => {
		setDormantDialog({ open: false });
		setPendingReactivate(false);
		setPendingConfirmUnlock(false);
	};

	const handleUnlockConfirm = () => {
		setUnlockDialog({ open: false });
		guardedSubmit(approveSubmittingRef, handleSubmitSalesRepId)(pendingReactivate, true);
	};

	const handleInactiveUnlockConfirm = () => {
		setInactiveUnlockDialog({ open: false });
		guardedSubmit(approveSubmittingRef, handleSubmitSalesRepId)(pendingReactivate, true);
	};

	const handleReject = async (reason) => {
		try {
			if (!shipto || !requestId) {
				toast.error("Missing required data (shipto or requestId).");
				return;
			}

			const trimmedReps = salesRepIds.map(r => r.trim()).filter(Boolean);

			if (isUnlocked) {
				if (trimmedReps.length === 0) { toast.error("At least one Sales Rep Email is required."); return; }
				const lowerCasedReps = trimmedReps.map(r => r.toLowerCase());
				if (new Set(lowerCasedReps).size !== lowerCasedReps.length) {
					toast.error("Duplicate Sales Rep Email is not allowed.");
					return;
				}
				if (!editedEmail || !editedEmail.trim()) { toast.error("Customer Email is required."); return; }
				if (!reason || !reason.trim()) { toast.error("Reason is required."); return; }
			} else {
				if (!reason || !reason.trim()) { toast.error("Reason is required."); return; }
			}

			const resolvedShipto = (shipto === editedShipto) ? shipto : editedShipto;
			const values = isUnlocked
				? { salesRepIds: trimmedReps, shipto: resolvedShipto, requestId, editedEmail: editedEmail || undefined, reason }
				: { shipto: resolvedShipto, requestId, reason };

			setRejectLoading(true);
			setReasonSubmitLoading(true);

			const res = await salesRepRejectAPI(values);
			if (res) {
				toast.success("Rejected successfully");
				setDialogObj({ open: false, obj: {}, index: null });
				setOpenReasonDialog(false);
				setReason('');
				handleGet();
			}
		} catch (e) {
			console.error('API Error:', e);
			toast.error("An error occurred while submitting.");
		} finally {
			setRejectLoading(false);
			setReasonSubmitLoading(false);
		}
	};

	const handleOpenReasonDialog  = () => setOpenReasonDialog(true);
	const handleCloseReasonDialog = () => { setOpenReasonDialog(false); setReason(''); };
	const guardedHandleReject = guardedSubmit(rejectSubmittingRef, handleReject);
	const handleSubmitReasonDialog = () => {
		if (!reason || !reason.trim()) { toast.error("Please enter a reason."); return; }
		guardedHandleReject(reason);
	};

	const handleBillingVerification = async (fromWelcomeStep = false) => {
		const setLoading = fromWelcomeStep ? setWelcomeLoading : setBillingLoading;
		try {
			if (!billingDate || billingDate == 'Invalid date') { toast.error("Please select a billing date."); return; }
			if (!shipto || !requestId) { toast.error("Missing required information."); return; }
			setLoading(true);
			const res = await billingConfirmationApi({ billingDate, shipto, requestId });
			if (res) { setDialogObj({ open: false, obj: {}, index: null }); handleGet(); }
		} catch (e) {
			console.error("API Error:", e);
			toast.error("An error occurred during billing verification.");
		} finally {
			setLoading(false);
		}
	};

	const handleAddonPlans = async () => {
		try {
			if (!salesPlan) { toast.error("Please select a Sales Plan option."); return; }
			if (!freeFreight) { toast.error("Please select a Free Freight option."); return; }
			if (!shipto || !requestId) { toast.error("Missing required data."); return; }
			setAddonLoading(true);
			const res = await addOnPlansMappingApi({ salesPlan, freeFreight, shipto, requestId });
			if (res) { setDialogObj({ open: false, obj: {}, index: null }); handleGet(); }
		} catch (e) {
			console.error("API error:", e);
			toast.error("An error occurred while submitting Add-on Plans.");
		} finally {
			setAddonLoading(false);
			setDialogObj({ open: false, obj: {}, index: null });
			handleGet();
		}
	};


	const anyDialogConfirmLoading = approveLoading;

	return (
		<div className="order-tracking">
			<style>{`
                .order-tracking {
                  max-width: 550px; margin: auto; font-family: "Arial", sans-serif;
                  color: #333; background: #fff; padding: 1.5rem; border-radius: 8px;
                }
                .step { display: flex; position: relative; margin-bottom: 2rem; }
                .circle-container { position: relative; width: 40px; display: flex; justify-content: center; }
                .circle {
                  width: 24px; height: 24px; border-radius: 50%; display: flex;
                  align-items: center; justify-content: center; font-size: 0.75rem;
                  font-weight: bold; transition: all 0.3s ease; background: #f5f5f5;
                  border: 2px solid #ccc; color: #777; z-index: 1;
                }
                .timeline {
                  position: absolute; top: 24px; bottom: -24px; left: 50%;
                  transform: translateX(-50%); width: 2px; background: #e0e0e0;
                }
                .step:last-child .timeline { display: none; }
                .step.active .circle { background: #1976d2; color: #fff; border: 2px solid #1976d2; }
                .step.completed .circle { background: #4CAF50; color: #fff; border: 2px solid #4CAF50; }
                .step-body { flex: 1; padding-left: 0.75rem; min-width: 0; }
                .step-header { cursor: pointer; margin-bottom: 0.3rem; }
                .step-title { font-weight: 600; font-size: 1rem; }
                .step-title-incomplete { font-weight: 600; font-size: 1rem; opacity: 0.5; }
                .step-date { font-size: 0.85rem; color: #666; }
                .step-content { max-height: 0; overflow: hidden; transition: max-height 0.3s ease; }
                .step-content.open {
                  max-height: 1000px; margin-top: 0.5rem;
                  border: 1px solid #ddd; padding: 15px; border-radius: 5px;
                  box-sizing: border-box; overflow-wrap: anywhere;
                }
                .step-content ul { margin: 0.5rem 0; padding-left: 1.2rem; }
                .step-content li {
                  margin-bottom: 0.3rem; font-size: 0.9rem; color: #444;
                  word-break: break-word; overflow-wrap: anywhere;
                }
                .step-content li span { word-break: break-word; overflow-wrap: anywhere; }
                .actions { margin-top: 0.6rem; display: flex; flex-direction: column; gap: 10px; }
                .actions button {
                  padding: 0.5rem 0.8rem; border: 1px solid #1976d2; border-radius: 4px;
                  background: #1976d2; color: white; font-size: 0.85rem; cursor: pointer;
                  display: flex; align-items: center; justify-content: center;
                }
                .actions button:hover { background: #1565c0; }
                .actions button:disabled {
                  background: #f0f0f0; border: 1px solid #ccc; color: #999; cursor: not-allowed;
                }
            `}</style>

			{/* ── Request Received ── */}
			<Step
				{...stepProps('request')}
				title="Request Received"
				content={renderSimpleLogs('request')}
			/>

			{/* ── Account Verification ── */}
			<Step
				{...stepProps('verification')}
				title="Account Verification"
				content={isDone('verification') ? (
					renderSimpleLogs('verification')
				) : (
					<div>
						<div style={{ marginTop: 10 }}>
							<InputLabel htmlFor="editedShipto" className={classes.labelTxt}>Enter the Shipto <span style={{ color: 'red' }}>*</span></InputLabel>
							<TextField id="editedShipto" placeholder="Enter the Shipto" type="number" variant="outlined"
							
								autoComplete="off" className={classes.searchInput} style={{ width: '90%' }}
								value={editedShipto} name="editedShipto" onChange={handleChange}
								inputProps={{ maxLength: 100 }} error={!!error.editedShipto} helperText={error.editedShipto} />
						</div>
						<div style={{ marginTop: 10 }}>
							<InputLabel htmlFor="editedEmail" className={classes.labelTxt}>Enter the Customers Email ID <span style={{ color: 'red' }}>*</span></InputLabel>
							<TextField id="editedEmail" placeholder="Enter the Customers Email ID" type="email" variant="outlined"
								autoComplete="off" className={classes.searchInput} style={{ width: '90%' }}
								value={editedEmail} name="editedEmail" onChange={handleChange}
								inputProps={{ maxLength: 100 }} error={!!error.editedEmail} helperText={error.editedEmail} />
						</div>

						{/* ── Multiple Sales Rep Email inputs ── */}
						<div style={{ marginTop: 10 }}>
							<InputLabel htmlFor="salesRepId-0" className={classes.labelTxt}>
								Sales Rep Email ID(s) <span style={{ color: 'red' }}>*</span>
							</InputLabel>

							{salesRepIds.map((repEmail, index) => (
								<Box key={index} display="flex" alignItems="center" style={{ width: '90%', marginBottom: 8 }}>
									<TextField
										id={`salesRepId-${index}`}
										type="email"
										variant="outlined"
										autoComplete="off"
										className={classes.searchInput}
										fullWidth
										placeholder="Enter Sales Rep Email"
										value={repEmail}
										name="salesRepId"
										onChange={(e) => handleSalesRepIdChange(index, e.target.value)}
										inputProps={{ maxLength: 100 }}
									/>
									{salesRepIds.length > 1 && (
										<IconButton onClick={() => removeSalesRepId(index)} size="small" style={{ marginLeft: 4 }}>
											<CloseIcon fontSize="small" />
										</IconButton>
									)}
								</Box>
							))}

							{error.salesRepId && (
								<div style={{ color: '#f44336', fontSize: '0.75rem', marginBottom: 8 }}>
									{error.salesRepId}
								</div>
							)}

							<Button onClick={addSalesRepId} size="small" style={{ textTransform: 'none', padding: 0 }}>
								+ Add another Sales Rep
							</Button>
						</div>

						<div style={{ marginTop: 10 }}>
							<InputLabel htmlFor="startDate" className={classes.labelTxt}>Enrollment Start Date <span style={{ color: 'red' }}>*</span></InputLabel>
							<MuiPickersUtilsProvider utils={DateFnsUtils} locale={locale}>
								<KeyboardDatePicker disableToolbar name="startDate" inputVariant="outlined"
									format={i18n.language === "en" ? "MM/dd/yyyy" : "dd/MM/yyyy"} margin="normal"
									id="billing-date-picker" className={classes.dateInput1}
									value={states?.date || null} maxDate={new Date()}
									minDate={states?.minEnrollmentDate ? new Date(states.minEnrollmentDate) : new Date("2000-01-01")}
									onChange={(date) => setStates({ ...states, date })}
									KeyboardButtonProps={{ "aria-label": "change date" }} autoOk fullWidth />
							</MuiPickersUtilsProvider>
						</div>
						<div style={{ marginTop: 10 }}>
							<InputLabel htmlFor="comments" className={classes.labelTxt}>Comments</InputLabel>
							<TextField id="comments" type="text" variant="outlined" autoComplete="off"
								className={classes.searchInput} style={{ width: '90%' }}
								value={comments} name="comments" onChange={handleChange} inputProps={{ maxLength: 100 }} />
						</div>
					</div>
				)}
				button={!isDone('verification') && (
					<div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: 10 }}>
						<button onClick={handleOpenReasonDialog} disabled={approveLoading || rejectLoading}
							style={{ color: "#305698", backgroundColor: "#fff", border: "1px solid #305698" }}>
							{rejectLoading ? <ButtonLoader /> : 'Reject'}
						</button>
						<button disabled={approveLoading || rejectLoading}
							style={{ color: "white", backgroundColor: "#1976d2", border: "1px solid #1976d2" }}
							onClick={() => guardedSubmit(approveSubmittingRef, handleSubmitSalesRepId)(false, false)}>
							{approveLoading ? <ButtonLoader /> : 'Approve'}
						</button>
					</div>
				)}
			/>

			{/* ── Internal Notification (Classic only; hidden for Unlocked & Trial) ── */}
			{!hideInternalNotification && (
				<Step
					{...stepProps('internal')}
					title="Internal Notification"
					content={renderSimpleLogs('internal')}
				/>
			)}

			{/* ── User Assignment ── */}
			<Step
				{...stepProps('assignment')}
				title="User Assignment"
				content={
					<ul>
						{logsFor('assignment').map((d, idx) => (
							<li key={idx}>
								<div>
									<span style={{ color: 'black' }}>{d.notes}</span><br />
									<span style={{ fontStyle: 'italic' }}>{moment(d.createdAt).format("ddd, Do MMM 'YY - h:mm A")}</span>
								</div>
								{d.start_date && <li><span>Enrollment Start Date: {moment(d.start_date).format("MM/DD/YYYY")}</span></li>}
								{d.loyalty_id && <li><span>Member ID: {d.loyalty_id}</span></li>}
							</li>
						))}
					</ul>
				}
			/>

			{/* ── Welcome Notification ── */}
			<Step
				{...stepProps('welcome')}
				title="Welcome Notification"
				date={
					logsFor('welcome').slice(-1)[0]?.notes?.toLowerCase().includes('not sent')
						? null
						: lastLogDate('welcome')
				}
				content={
					<ul>
						{logsFor('welcome').map((d, idx) => (
							<li key={idx}>
								<div>
									<span style={{ color: 'black' }}>{d.notes}</span><br />
									{!d.notes?.toLowerCase().includes('not sent') && (
										<span style={{ fontStyle: 'italic' }}>{moment(d.createdAt).format("ddd, Do MMM 'YY - h:mm A")}</span>
									)}
								</div>
							</li>
						))}
					</ul>
				}
				button={!isDone('welcome') && (
					<button onClick={() => guardedSubmit(welcomeSubmittingRef, handleBillingVerification)(true)} disabled={welcomeLoading}>
						{welcomeLoading ? <ButtonLoader /> : 'Confirm'}
					</button>
				)}
			/>

			{/* ── Billing Verification (Classic only) ── */}
			{!hideInternalNotification && (
				<Step
					{...stepProps('billing')}
					title="Billing Verification"
					content={isDone('billing') ? (
						<ul>
							{logsFor('billing').map((d, idx) => (
								<li key={idx}>
									<div>
										<span style={{ color: 'black' }}>{d.notes}</span><br />
										<span style={{ fontStyle: 'italic' }}>{moment(d.createdAt).format("ddd, Do MMM 'YY - h:mm A")}</span>
									</div>
									<li><span>Billing Start Date: {d.value}</span></li>
								</li>
							))}
						</ul>
					) : (
						<div>
							<MuiPickersUtilsProvider utils={DateFnsUtils} locale={locale}>
								<KeyboardDatePicker disableToolbar name="billingDate" inputVariant="outlined"
									format={i18n.language === "en" ? "MM/dd/yyyy" : "dd/MM/yyyy"} margin="normal"
									label="Billing Date" id="billing-date-picker" className={classes.dateInput}
									value={billingDate}
									minDate={states?.minBillingDate ? new Date(states.minBillingDate) : new Date()}
									onChange={date => setFormValues({ ...formValues, billingDate: moment(date).format("YYYY-MM-DD") })}
									KeyboardButtonProps={{ "aria-label": "change date" }} autoOk fullWidth />
							</MuiPickersUtilsProvider>
						</div>
					)}
					button={!isDone('billing') && (
						<button onClick={() => guardedSubmit(billingSubmittingRef, handleBillingVerification)(false)} disabled={billingLoading}>
							{billingLoading ? <ButtonLoader /> : 'Confirm'}
						</button>
					)}
				/>
			)}

			{/* ── Add-on Plans (last step for every plan) ── */}
			<Step
				{...stepProps('addon')}
				title="Add-on Plans"
				content={isDone('addon') ? (
					renderSimpleLogs('addon')
				) : (
					<ul>
						<li style={{ marginBottom: '12px' }}>
							<label style={{ fontWeight: '600', display: 'block', marginBottom: '4px' }}>Sales Plan:</label>
							{['Apply', 'Skip'].map(v => (
								<label key={v} style={{ marginRight: 20 }}>
									<input type="radio" name="salesPlan" value={v} style={{ marginRight: 6 }}
										checked={formValues.salesPlan === v} onChange={handleChange} />
									{v}
								</label>
							))}
						</li>
						<li style={{ marginBottom: '12px' }}>
							<label style={{ fontWeight: '600', display: 'block', marginBottom: '4px' }}>FF/Surcharge:</label>
							{['Apply', 'Skip'].map(v => (
								<label key={v} style={{ marginRight: 20 }}>
									<input type="radio" name="freeFreight" value={v} style={{ marginRight: 6 }}
										checked={formValues.freeFreight === v} onChange={handleChange} />
									{v}
								</label>
							))}
						</li>
					</ul>
				)}
				button={!isDone('addon') && (
					<button onClick={() => guardedSubmit(addonSubmittingRef, handleAddonPlans)()} disabled={addonLoading}>
						{addonLoading ? <ButtonLoader /> : 'Confirm'}
					</button>
				)}
			/>


			{/* ═══════════════════════════════════════════════════════════════════ */}
			{/* DIALOGS                                                            */}
			{/* ═══════════════════════════════════════════════════════════════════ */}

			{/* Reject reason dialog */}
			<Dialog open={openReasonDialog} onClose={handleCloseReasonDialog}>
				<DialogTitle>Reason for Rejection</DialogTitle>
				<DialogContent>
					<TextField autoFocus margin="dense" label="Reason" type="text" fullWidth variant="outlined"
						value={reason} onChange={(e) => setReason(e.target.value)} required />
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseReasonDialog} color="secondary" disabled={reasonSubmitLoading}>Cancel</Button>
					<Button onClick={handleSubmitReasonDialog} color="primary" disabled={reasonSubmitLoading}>
						{reasonSubmitLoading ? <ButtonLoader /> : 'Submit'}
					</Button>
				</DialogActions>
			</Dialog>

			{/* ── DORMANT DIALOG (both Unlock & non-Unlock) ── */}
			<Dialog open={dormantDialog.open} onClose={handleDormantCancel} maxWidth="sm">
				<div style={{ padding: 20 }}>
					<h2 style={{ marginBottom: 10 }}>{t('Reactivate Dormant Account?')}</h2>
					<p>
						This customer is currently Dormant in Thrive Rewards. Reactivate their account to continue
						enrollment in Thrive {isUnlocked ? "Unlocked" : "Signature"}.
					</p>
					<div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
						<Button onClick={handleDormantCancel} variant="outlined" disabled={anyDialogConfirmLoading}
							style={{ marginRight: 10, backgroundColor: "red", color: "white" }}>
							Cancel
						</Button>
						<Button onClick={handleDormantConfirm} variant="contained" disabled={anyDialogConfirmLoading}
							style={{ backgroundColor: "green", color: "white" }}>
							{anyDialogConfirmLoading ? <ButtonLoader /> : 'Reactivate & Continue'}
						</Button>
					</div>
				</div>
			</Dialog>

			{/* ── UNLOCK DIALOG: not in whitelist (Unlock only) ── */}
			{isUnlocked && (
				<Dialog open={unlockDialog.open} onClose={() => setUnlockDialog({ open: false })} maxWidth="sm">
					<div style={{ padding: 20 }}>
						<h2 style={{ marginBottom: 10 }}>{t('⚠️ Eligibility Review Needed')}</h2>
						<p>
							This customer is not on the Prequalified Customer List. Please validate eligibility
							or obtain the appropriate approval before proceeding with enrollment.
						</p>
						<div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
							<Button onClick={() => setUnlockDialog({ open: false })} variant="outlined" disabled={anyDialogConfirmLoading}
								style={{ marginRight: 10, backgroundColor: "red", color: "white" }}>
								Cancel
							</Button>
							<Button onClick={handleUnlockConfirm} variant="contained" disabled={anyDialogConfirmLoading}
								style={{ backgroundColor: "green", color: "white" }}>
								{anyDialogConfirmLoading ? <ButtonLoader /> : 'Continue'}
							</Button>
						</div>
					</div>
				</Dialog>
			)}

			{/* ── INACTIVE UNLOCK DIALOG: in whitelist but Ineligible/Inactive/Hard Ineligible (Unlock only) ── */}
			{isUnlocked && (
				<Dialog open={inactiveUnlockDialog.open} onClose={() => setInactiveUnlockDialog({ open: false })} maxWidth="sm">
					<div style={{ padding: 20 }}>
						<h2 style={{ marginBottom: 10 }}>{t('⚠️ Account Status Alert')}</h2>
						<p>
							This customer exists in the Prequalified Customer List but their review status is Ineligible,
							or Hard Ineligible. Do you want to activate this account and proceed with enrollment?
						</p>
						<div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
							<Button onClick={() => setInactiveUnlockDialog({ open: false })} variant="outlined" disabled={anyDialogConfirmLoading}
								style={{ marginRight: 10, backgroundColor: "red", color: "white" }}>
								Cancel
							</Button>
							<Button onClick={handleInactiveUnlockConfirm} variant="contained" disabled={anyDialogConfirmLoading}
								style={{ backgroundColor: "green", color: "white" }}>
								{anyDialogConfirmLoading ? <ButtonLoader /> : 'Activate & Continue'}
							</Button>
						</div>
					</div>
				</Dialog>
			)}
		</div>
	);
}

function Step({ stepKey, displayNumber, isActive, isCompleted, expanded, toggleStep, title, date, content, button }) {
	const isExpanded = isActive || expanded;

	return (
		<div className={`step ${isActive ? "active" : ""} ${isCompleted ? "completed" : ""}`}>
			<div className="circle-container">
				<div className="circle">{isCompleted ? "✓" : displayNumber}</div>
				<div className="timeline"></div>
			</div>
			<div className="step-body">
				<div className="step-header" onClick={() => toggleStep(stepKey)}>
					<div className={(isCompleted || isActive) ? "step-title" : "step-title-incomplete"}>
						{title}
					</div>
					<div className="step-date">{date ? moment(date).format("ddd, Do MMM 'YY - h:mm A") : ""}</div>
				</div>
				<div className={`step-content ${isExpanded ? "open" : ""}`}>
					{content}
					<div className="actions">{button}</div>
				</div>
			</div>
		</div>
	);
}